import { useCallback, useEffect, useState } from 'react';
import { Plus } from 'lucide-react';
import Modal from '../ui/Modal';
import Badge from '../ui/Badge';
import { api } from '../../lib/api';
import { Field, TextInput, TextArea, Select, MoneyInput, BooksAccountSelect, History, Empty, Money, fmtDate, today, btnCls, btn2Cls, thCls, tdCls, KIND_LABEL } from './shared';
import { BooksChip } from './LedgerTab';

// Vendor bills (Pinnacle, OEC, Allen, carriers, CPA, software): who pays, who paid and from
// where, the receipt, and re-billing to Concria. A bill can only be marked paid once, and
// the same vendor + bill number cannot be entered twice.

const WHO_PAYS = { ccs: 'CCS', concria: 'Concria Oy (re-bill)', split: 'Split' };
const PAID_BY = { 'CCS Relay': 'CCS Relay account', Chad: 'Chad (to reimburse)', Steve: 'Steve (to reimburse)', Chase: 'Chase (to reimburse)', 'Concria Oy': 'Concria Oy paid directly', Other: 'Other' };
const VARIANT = { open: 'warning', paid: 'success', void: 'default' };
// Payers whose money is CCS's: a bill they paid posts to Books (the others never do).
const CCS_PAYERS = ['CCS Relay', 'Chad', 'Steve', 'Chase'];

function BillChip({ bill, posting }) {
  if (bill.deleted_at || bill.status === 'void') return null;
  if (bill.status === 'open') return <BooksChip posting={posting} waitingFor="Posts when paid" />;
  if (!bill.books_entry_id && !CCS_PAYERS.includes(bill.paid_by)) return null;
  return <BooksChip posting={posting} entryId={bill.books_entry_id} date={bill.paid_date} />;
}

export default function BillsTab({ me, accounts, posting, onChanged }) {
  const [bills, setBills] = useState(null);
  const [edit, setEdit] = useState(null);
  const [pay, setPay] = useState(null);
  const [post, setPost] = useState(null);
  const [error, setError] = useState('');
  const load = useCallback(async () => {
    try { setBills((await api.get('/intercompany/bills')).bills); setError(''); } catch (e) { setError(e.message); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const done = () => { setEdit(null); setPay(null); setPost(null); load(); onChanged(); };
  const act = async (fn) => { try { await fn(); done(); } catch (e) { setError(e.message); } };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-[var(--text-secondary)]">Enter a bill once. Mark it paid once. Re-bill Concria's share to the ledger.</p>
        <button className={btnCls} onClick={() => setEdit({})}><Plus size={14} /> Add bill</button>
      </div>
      {error && <p className="text-sm text-rose-600">{error}</p>}
      {!bills ? <p className="text-sm text-[var(--text-secondary)]">Loading...</p> : bills.length === 0 ? <Empty>No bills entered.</Empty> : (
        <div className="overflow-x-auto rounded-xl border border-[var(--border)] bg-white dark:bg-slate-900">
          <table className="w-full min-w-[820px] text-sm">
            <thead className="bg-gray-50 dark:bg-slate-800"><tr>
              <th className={thCls}>Bill</th><th className={thCls}>Who pays</th><th className={`${thCls} text-right`}>Amount</th><th className={thCls}>Status</th><th className={thCls}>Actions</th>
            </tr></thead>
            <tbody>
              {bills.map((b) => (
                <tr key={b.id} className="border-t border-[var(--border)]">
                  <td className={tdCls}>
                    <button className="text-left" onClick={() => setEdit(b)}>
                      <div className="text-[var(--text-primary)]">{b.vendor}{b.bill_no ? ` #${b.bill_no}` : ''}</div>
                      <div className="text-xs text-[var(--text-secondary)]">{[b.bill_date && fmtDate(b.bill_date), b.due_date && `due ${fmtDate(b.due_date)}`, b.description].filter(Boolean).join(' · ')}</div>
                    </button>
                  </td>
                  <td className={tdCls}>{WHO_PAYS[b.who_pays]}{b.rebill_line_id && <div className="text-xs text-emerald-700 dark:text-emerald-400">Re-billed to Concria</div>}</td>
                  <td className={`${tdCls} text-right`}><Money cents={b.amount_cents} /></td>
                  <td className={tdCls}>
                    <Badge variant={VARIANT[b.status]}>{b.status === 'paid' ? 'Paid' : b.status === 'void' ? 'Void' : 'Open'}</Badge>
                    {b.status === 'paid' && <div className="text-xs text-[var(--text-secondary)]">{fmtDate(b.paid_date)} by {b.paid_by}{b.paid_from ? ` (${b.paid_from})` : ''}</div>}
                    {b.receipt_url && <a className="text-xs text-primary" href={b.receipt_url} target="_blank" rel="noreferrer">receipt</a>}
                    <div className="mt-1"><BillChip bill={b} posting={posting} /></div>
                  </td>
                  <td className={tdCls}>
                    <div className="flex flex-wrap gap-1">
                      {b.status === 'open' && <button className={btn2Cls} onClick={() => setPay(b)}>Mark paid</button>}
                      {b.status !== 'void' && !b.rebill_line_id && b.who_pays !== 'ccs' && <button className={btn2Cls} onClick={() => act(() => api.post(`/intercompany/bills/${b.id}/rebill`, { kind: 'storage' }))}>Re-bill Concria</button>}
                      {me.can_post && b.status === 'paid' && !b.books_entry_id && CCS_PAYERS.includes(b.paid_by) && <button className={btn2Cls} onClick={() => setPost(b)}>Post to Books</button>}
                      {me.can_approve && b.status !== 'void' && !b.books_entry_id && <button className={btn2Cls} onClick={() => window.confirm('Void this bill?') && act(() => api.post(`/intercompany/bills/${b.id}/void`, {}))}>Void</button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {edit && <BillModal bill={edit.id ? edit : null} accounts={accounts} onClose={() => setEdit(null)} onSaved={done} />}
      {pay && <PayModal bill={pay} onClose={() => setPay(null)} onSaved={done} />}
      {post && <PostModal bill={post} accounts={accounts} onClose={() => setPost(null)} onSaved={done} />}
      <p className="text-xs text-[var(--text-secondary)]">Re-billing creates an "awaiting response" line on the Ledger tab ({KIND_LABEL.storage.toLowerCase()} by default; change the kind there).</p>
    </div>
  );
}

const FIELDS = ['vendor', 'bill_no', 'bill_date', 'due_date', 'description', 'amount_cents', 'who_pays', 'receipt_url', 'note', 'expense_code'];

// The Books expense account a paid bill posts to (empty = the posting map's default, 9999 to review).
function ExpenseCodeSelect({ accounts, value, onChange, disabled }) {
  const list = (accounts || []).filter((a) => ['cogs', 'expense', 'asset'].includes(a.type) && !a.is_cash && !['1300', '2000'].includes(a.code));
  if (disabled) {
    const a = list.find((x) => x.code === value);
    return <p className="text-sm text-[var(--text-primary)]">{value ? `${value}${a ? ` ${a.name}` : ''}` : 'Default (9999, to review)'} <span className="text-xs text-[var(--text-secondary)]">(posted; change it in Books)</span></p>;
  }
  return (
    <Select value={value || ''} onChange={(v) => onChange(v || '')}
      options={Object.fromEntries(list.map((a) => [a.code, `${a.code} ${a.name}`]))} placeholder="Default (9999, to review)" />
  );
}

function BillModal({ bill, accounts, onClose, onSaved }) {
  const [f, setF] = useState(() => (bill ? Object.fromEntries(FIELDS.map((k) => [k, bill[k] ?? ''])) : { who_pays: 'ccs', bill_date: today() }));
  const [error, setError] = useState('');
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));
  const save = async () => {
    try {
      const body = {};
      for (const k of FIELDS) {
        const v = f[k];
        if (bill && String(v ?? '') === String(bill[k] ?? '')) continue;
        if (!bill && (v === '' || v == null)) continue;
        body[k] = v === '' ? null : v;
      }
      if (bill) await api.patch(`/intercompany/bills/${bill.id}`, { ...body, revision: bill.updated_at });
      else await api.post('/intercompany/bills', body);
      onSaved();
    } catch (e) { setError(e.message); }
  };
  const remove = async () => {
    if (!window.confirm('Delete this bill?')) return;
    try { await api.delete(`/intercompany/bills/${bill.id}`); onSaved(); } catch (e) { setError(e.message); }
  };
  return (
    <Modal title={bill ? 'Bill' : 'Add a bill'} onClose={onClose}>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="Vendor"><TextInput value={f.vendor} onChange={set('vendor')} placeholder="Example Freight Co." /></Field>
        <Field label="Bill / invoice no."><TextInput value={f.bill_no} onChange={set('bill_no')} /></Field>
        <Field label="Bill date"><TextInput type="date" value={f.bill_date} onChange={set('bill_date')} /></Field>
        <Field label="Due date"><TextInput type="date" value={f.due_date} onChange={set('due_date')} /></Field>
        <Field label="Amount (USD)"><MoneyInput cents={f.amount_cents || null} onChange={set('amount_cents')} /></Field>
        <Field label="Who pays"><Select value={f.who_pays} onChange={set('who_pays')} options={WHO_PAYS} /></Field>
        <Field label="Description" className="sm:col-span-2"><TextInput value={f.description} onChange={set('description')} /></Field>
        <Field label="Books expense account (posts when paid)" className="sm:col-span-2">
          <ExpenseCodeSelect accounts={accounts} value={f.expense_code} onChange={set('expense_code')} disabled={!!bill?.books_entry_id} />
        </Field>
        <Field label="Receipt / bill link" className="sm:col-span-2"><TextInput value={f.receipt_url} onChange={set('receipt_url')} placeholder="https://" /></Field>
        <Field label="Note" className="sm:col-span-2"><TextArea value={f.note} onChange={set('note')} rows={2} /></Field>
      </div>
      {error && <p className="mt-3 text-sm text-rose-600">{error}</p>}
      <div className="mt-4 flex gap-2">
        <button className={btnCls} onClick={save}>{bill ? 'Save' : 'Add'}</button>
        {bill && <button className={btn2Cls} onClick={remove}>Delete</button>}
      </div>
      {bill && <History table="bills" id={bill.id} />}
    </Modal>
  );
}

function PayModal({ bill, onClose, onSaved }) {
  const [f, setF] = useState({ paid_date: today(), paid_by: 'CCS Relay' });
  const [error, setError] = useState('');
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));
  const save = async () => {
    try {
      const body = Object.fromEntries(Object.entries(f).filter(([, v]) => v !== '' && v != null));
      await api.post(`/intercompany/bills/${bill.id}/pay`, body); onSaved();
    } catch (e) { setError(e.message); }
  };
  return (
    <Modal title={`Mark paid: ${bill.vendor}${bill.bill_no ? ` #${bill.bill_no}` : ''}`} onClose={onClose}>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="Paid on"><TextInput type="date" value={f.paid_date} onChange={set('paid_date')} /></Field>
        <Field label="Paid by"><Select value={f.paid_by} onChange={set('paid_by')} options={PAID_BY} /></Field>
        <Field label="Paid from (no card or account numbers)"><TextInput value={f.paid_from} onChange={set('paid_from')} placeholder="Relay checking" /></Field>
        <Field label="Payment reference"><TextInput value={f.payment_ref} onChange={set('payment_ref')} /></Field>
        <Field label="Receipt link" className="sm:col-span-2"><TextInput value={f.receipt_url} onChange={set('receipt_url')} placeholder="https://" /></Field>
      </div>
      {error && <p className="mt-3 text-sm text-rose-600">{error}</p>}
      <button className={`${btnCls} mt-4`} onClick={save}>Mark paid</button>
    </Modal>
  );
}

function PostModal({ bill, accounts, onClose, onSaved }) {
  const [acct, setAcct] = useState(null);
  const [error, setError] = useState('');
  const save = async (allowBefore = false) => {
    try { await api.post(`/intercompany/bills/${bill.id}/post-to-books`, { account_id: acct, allow_before_cutover: allowBefore }); onSaved(); } catch (e) {
      if (e.status === 409 && /cutover/.test(e.message) && window.confirm(`${e.message}\n\nPost anyway?`)) return save(true);
      setError(e.message);
    }
  };
  return (
    <Modal title="Post bill to Books" onClose={onClose}>
      <Field label="Expense account (the credit side follows who paid)"><BooksAccountSelect accounts={accounts} value={acct} onChange={setAcct} types={['cogs', 'expense', 'asset']} /></Field>
      {error && <p className="mt-3 text-sm text-rose-600">{error}</p>}
      <button className={`${btnCls} mt-4`} disabled={!acct} onClick={() => save(false)}>Post</button>
    </Modal>
  );
}
